'use client';

import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, IconButton, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined';
import dayjs from 'dayjs';
import DataTable from '../../../../components/DataTable';
import { fleetApi } from '../../../../api/axiosClient';
import { useLanguage } from '../../../../utils/i18n';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const formatCurrency = (val) => {
  if (val === undefined || val === null || val === '') return '-';
  const num = Number(val);
  return isNaN(num) ? '-' : 'Rp ' + num.toLocaleString('id-ID');
};

export default function FuelInformationDialog({ open, onClose, licensePlate, vehicleId, vehicleName }) {
  const { t } = useLanguage();

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchField, setSearchField] = useState('all');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'fuel_type_name', label: 'Fuel Type', tableName: 'm_fuel_type', fieldName: 'name' },
      { value: 'price', label: 'Price' },
      { value: 'valid_at', label: 'Valid At' }
    ],
    [t]
  );

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchValue(String(searchRaw ?? '').trim());
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchRaw, searchField]);

  // Fetch fuel price history for current vehicle/license plate
  useEffect(() => {
    if (!open) return;
    if (!licensePlate && !vehicleId) {
      setRows([]);
      setTotalRecords(0);
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    const fetchHistory = async () => {
      setLoading(true);
      try {
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;

        const columns = [];
        if (licensePlate) {
          columns.push({ name: 'license_plate', logic_operator: '=', operator: 'AND', value: String(licensePlate) });
        } else if (vehicleId) {
          columns.push({ name: 'vehicle_id', logic_operator: '=', operator: 'AND', value: String(vehicleId) });
        }

        if (searchField !== 'all' && searchValue) {
          const opt = searchFieldOptions.find((o) => o.value === searchField);
          const searchCol = {
            name: opt?.fieldName || searchField,
            logic_operator: 'like',
            operator: 'AND',
            value: searchValue
          };
          if (opt?.tableName) searchCol.table_name = opt.tableName;
          columns.push(searchCol);
        }

        let filters = [];
        if (searchField === 'all' && searchValue) {
          filters = [
            { name: 'name', table_name: 'm_fuel_type', value: searchValue },
            { name: 'price', value: searchValue },
            { name: 'valid_at', value: searchValue }
          ];
        }

        const payload = {
          paging: { start, length },
          orders: { columns: ['id'], ascending: false },
          joins: [
            { name: 'company', column_results: ['name'] },
            { name: 'vehicle', column_results: ['name', 'license_plate'] },
            { name: 'fuel_type', column_results: ['name', 'code', 'category', 'description', 'uom_type'] }
          ],
          columns: columns,
          filters: filters
        };

        const resp = await fleetApi.post('/fuel-price-history/search' + getTzParam(), payload, { signal: controller.signal });
        const data = resp?.data ?? null;
        const fetchedRows = Array.isArray(data) ? data : (data?.data ?? []);
        const total = Array.isArray(data) ? fetchedRows.length : (data?.totalRecord ?? data?.totalRecords ?? data?.dataSize ?? 0);

        setRows(fetchedRows);
        setTotalRecords(total);
      } catch (err) {
        if (err?.name !== 'AbortError' && err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
          console.error('Failed to fetch fuel history for vehicle:', err);
          setRows([]);
          setTotalRecords(0);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchHistory();
    return () => controller.abort();
  }, [open, licensePlate, vehicleId, paginationModel, searchField, searchValue, searchFieldOptions]);

  const dtColumns = useMemo(
    () => [
      {
        field: 'fuel_type_name',
        headerName: 'Fuel Type',
        flex: 1.2,
        minWidth: 140,
        valueGetter: (value, row) => row.fuel_type_name || row?.fuel_type_join?.name || '-'
      },
      {
        field: 'price',
        headerName: 'Price (IDR)',
        flex: 1,
        minWidth: 120,
        renderCell: (params) => formatCurrency(params.row.price)
      },
      {
        field: 'valid_at',
        headerName: 'Valid At',
        flex: 1,
        minWidth: 130,
        valueGetter: (value, row) => {
          const val = row.valid_at;
          if (!val || String(val).startsWith('0001-01-01') || String(val).startsWith('1900-01-01')) return '-';
          return dayjs(val).isValid() ? dayjs(val).format('DD MMM YYYY') : '-';
        }
      },
      {
        field: 'fuel_type_desc',
        headerName: 'Description',
        flex: 1.5,
        minWidth: 160,
        valueGetter: (value, row) => row.fuel_type_desc || row?.fuel_type_join?.description || '-'
      }
    ],
    []
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Fuel Price History
          </Typography>
          {(licensePlate || vehicleName) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, p: 1, bgcolor: '#f1f7fd', borderRadius: 1 }}>
              {' '}
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {licensePlate === vehicleName ? licensePlate : [licensePlate, vehicleName].filter(Boolean).join(' • ')}
              </Typography>
            </Box>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2, minHeight: 360 }}>
        {!loading && rows.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              my: 2,
              textAlign: 'center',
              bgcolor: '#f8fafc',
              border: '1px dashed #cbd5e1',
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: '#e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
              }}
            >
              <LocalGasStationOutlinedIcon sx={{ fontSize: 32, color: '#64748b' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>
              No Fuel Price History Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
              This vehicle does not have any fuel price history records yet. Please <strong>assign</strong> a fuel price first.
            </Typography>
          </Paper>
        ) : (
          <DataTable
            title="Vehicle Fuel Price History"
            rows={rows}
            columns={dtColumns}
            rowCount={totalRecords}
            loading={loading}
            pageSizeOptions={[5, 10, 25]}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            searchQuery={searchRaw}
            onSearch={({ field, value }) => {
              setSearchField(field || 'all');
              setSearchRaw(value ?? '');
              setPaginationModel((p) => ({ ...p, page: 0 }));
            }}
            searchFieldOptions={searchFieldOptions}
            selectedSearchField={searchField}
            onSearchFieldChange={(f) => {
              setSearchField(f);
              setSearchRaw('');
              setSearchValue('');
              setPaginationModel((p) => ({ ...p, page: 0 }));
            }}
            searchLabel="Search history"
            hideAddButton={true}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
